"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  listGalleryImages,
  deleteFromGallery,
  getGalleryCacheStats,
  cleanupGalleryCache,
} from "@/client/utils/galleryCache";
import { PageContainer } from "@/client/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FilePreviewDialog, UploadFileItem, useFilePreview } from "@/client/components/FileUpload";

interface GalleryItemViewModel {
  url: string;
  objectUrl: string;
  modelKey: string;
  size: number;
  createdAt: number;
}

interface GalleryStats {
  totalSize: number;
  count: number;
}

/**
 * 将字节数格式化为易读的字符串（KB / MB）
 */
const formatFileSize = (size: number): string => {
  if (!Number.isFinite(size) || size <= 0) {
    return "0 KB";
  }
  const kb = size / 1024;
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }
  const mb = kb / 1024;
  if (mb < 1024) {
    return `${mb.toFixed(2)} MB`;
  }
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};

/**
 * 将时间戳格式化为本地时间字符串
 */
const formatTimestamp = (timestamp: number): string => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  } catch {
    return new Date(timestamp).toLocaleString();
  }
};

const GalleryPageClient = () => {
  // 图库图片列表（已转换为可直接展示的对象 URL）
  const [items, setItems] = useState<GalleryItemViewModel[]>([]);
  // 图库缓存统计信息：总大小与数量
  const [stats, setStats] = useState<GalleryStats>({ totalSize: 0, count: 0 });
  // 页面整体加载状态
  const [loading, setLoading] = useState(false);
  // 清理缓存按钮的加载状态
  const [cleaning, setCleaning] = useState(false);
  // 当前待删除的图片条目（控制确认弹窗）
  const [pendingDelete, setPendingDelete] = useState<GalleryItemViewModel | null>(
    null,
  );
  // 删除操作进行中的状态
  const [deleting, setDeleting] = useState(false);
  const getPreviewTitle = useCallback(
    (item: UploadFileItem) => item.id ?? item.file?.name ?? "生成图片",
    [],
  );
  const {
    previewItem,
    previewTitle,
    previewSrc,
    isVideoPreview,
    openPreview,
    closePreview,
  } = useFilePreview({ getTitle: getPreviewTitle });

  /**
   * 释放上一轮生成的对象 URL，避免内存泄漏
   */
  const revokeObjectUrls = useCallback((list: GalleryItemViewModel[]) => {
    list.forEach((item) => {
      try {
        URL.revokeObjectURL(item.objectUrl);
      } catch {
        // ignore
      }
    });
  }, []);

  /**
   * 从 IndexedDB 中加载图片列表和缓存统计信息
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 先清理旧的对象 URL
      setItems((prev) => {
        revokeObjectUrls(prev);
        return [];
      });

      // 并行获取图片记录与缓存统计
      const [records, cacheStats] = await Promise.all([
        listGalleryImages(),
        getGalleryCacheStats(),
      ]);

      const mapped: GalleryItemViewModel[] = records.map((record) => ({
        url: record.url,
        objectUrl: URL.createObjectURL(record.blob),
        modelKey: record.modelKey,
        size: record.size,
        createdAt: record.createdAt,
      }));

      setItems(mapped);
      setStats(cacheStats);
    } catch (error) {
      console.error("[GalleryPage] 加载图库数据失败", error);
      toast.error("加载图库失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [revokeObjectUrls]);

  /**
   * 首次挂载时加载图库数据，并在卸载时释放对象 URL
   */
  useEffect(() => {
    void loadData();
    return () => {
      setItems((prev) => {
        revokeObjectUrls(prev);
        return prev;
      });
    };
  }, [loadData, revokeObjectUrls]);

  useEffect(() => {
    if (!previewItem) {
      return;
    }
    const exists = items.some(
      (item) => item.objectUrl === previewItem.src || item.url === previewItem.id,
    );
    if (!exists) {
      closePreview();
    }
  }, [closePreview, items, previewItem]);

  /**
   * 当前是否为空状态
   */
  const isEmpty = useMemo(() => !loading && items.length === 0, [loading, items]);

  /**
   * 触发删除确认弹窗
   */
  const openDeleteDialog = (item: GalleryItemViewModel) => {
    setPendingDelete(item);
  };

  /**
   * 关闭删除确认弹窗
   */
  const closeDeleteDialog = () => {
    setPendingDelete(null);
  };

  const openPreviewDialog = useCallback(
    (item: GalleryItemViewModel) => {
      openPreview({
        id: item.modelKey || item.url,
        src: item.objectUrl,
      });
    },
    [openPreview],
  );

  /**
   * 确认删除指定图片：调用 deleteFromGallery 并刷新列表与统计
   */
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteFromGallery(pendingDelete.url);
      toast.success("已删除该图片");
      setPendingDelete(null);
      await loadData();
    } catch (error) {
      console.error("[GalleryPage] 删除图片失败", error);
      toast.error("删除失败，请稍后重试");
    } finally {
      setDeleting(false);
    }
  };

  /**
   * 清理过期缓存，并刷新页面数据
   */
  const handleCleanupCache = async () => {
    setCleaning(true);
    try {
      await cleanupGalleryCache();
      toast.success("已清理过期缓存");
      await loadData();
    } catch (error) {
      console.error("[GalleryPage] 清理缓存失败", error);
      toast.error("清理缓存失败，请稍后重试");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <PageContainer spacing={3}>
      <div className="flex flex-col gap-3 px-4 md:px-6 py-4">
        {/* 顶部统计区：展示图片数量与占用空间，并提供操作按钮 */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold">图片图库</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">共 {stats.count} 张图片</Badge>
              <Badge>已用空间 {formatFileSize(stats.totalSize)}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={() => {
                  void loadData();
                }}
              >
                {loading ? "加载中..." : "刷新"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={cleaning || loading}
                onClick={() => {
                  void handleCleanupCache();
                }}
              >
                {cleaning ? "清理中..." : "清理过期缓存"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 主体图片网格区域 */}
        <Card className="flex-1 min-h-[260px]">
          <CardContent className="pt-4">
            {isEmpty ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 py-16 text-center">
                <p className="text-sm text-muted-foreground">
                  暂无图片，使用 Gemini 图像生成模型后图片会自动保存到这里。
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {items.map((item) => (
                  <div
                    key={item.url}
                    className="group flex flex-col overflow-hidden rounded-md border bg-card shadow-sm"
                  >
                    {/* 图片缩略图 */}
                    <button
                      type="button"
                      className="aspect-[4/3] w-full overflow-hidden bg-muted cursor-pointer"
                      onClick={() => openPreviewDialog(item)}
                    >
                      <img
                        src={item.objectUrl}
                        alt={item.modelKey || "生成图片"}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </button>
                    {/* 图片元信息与操作 */}
                    <div className="flex items-center justify-between gap-2 px-2.5 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium">
                          {item.modelKey || "未知模型"}
                        </p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {formatTimestamp(item.createdAt)} · {formatFileSize(item.size)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="shrink-0 text-destructive hover:text-destructive"
                        onClick={() => openDeleteDialog(item)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <FilePreviewDialog
        open={Boolean(previewItem)}
        onOpenChange={(open) => {
          if (!open) {
            closePreview();
          }
        }}
        title={previewTitle}
        src={previewSrc}
        isVideo={isVideoPreview}
        emptyText="暂无可预览内容"
        closeText="关闭预览"
      />

      {/* 删除确认对话框 */}
      <Dialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) {
            closeDeleteDialog();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除图片</DialogTitle>
            <DialogDescription>
              此操作仅会从当前浏览器的本地图库缓存中删除图片，不会影响服务器或其他设备上的数据。
            </DialogDescription>
          </DialogHeader>
          {pendingDelete ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                模型：{pendingDelete.modelKey || "未知模型"}
              </p>
              <p className="text-xs text-muted-foreground">
                创建时间：{formatTimestamp(pendingDelete.createdAt)}
              </p>
              <p className="text-xs text-muted-foreground">
                图片大小：{formatFileSize(pendingDelete.size)}
              </p>
            </div>
          ) : null}
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              disabled={deleting}
              onClick={closeDeleteDialog}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => {
                void handleConfirmDelete();
              }}
            >
              {deleting ? "删除中..." : "确认删除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default GalleryPageClient;
